/**
 * 투자제안서 투자분석/자금계획 순수 계산 모듈. Vue 의존 없음.
 * 금액 단위 = 만원, 면적 = 평, 율 = 퍼센트 숫자(4.6, 60).
 * 입력 item: sell_price=만원(그대로), total_income=월 총수익(만원) → NOI 는 ×12 연환산.
 *
 * 신축/리모델링 단가 출처: spacevision.co.kr 건축계산기(construction/remodel/design).
 * 기준: 2026년 상반기 시장단가(한국건설기술연구원 단가 참조). 부가세 10% 별도.
 * 최신 갱신 레버: 건설공사비지수(KOSIS OpenAPI orgId=397) — 추후 자동 escalation 예정.
 * 기본 가정: 구조=RC, 마감=표준, 지역=서울(보정 1.0), 층수=물건 지상층수, 용도=main_purpose 매핑.
 */

const RESIDENTIAL_RE = /주택|주거|아파트|공동주택|다세대|연립|단독|도시형생활/;
export function classifyAcqTaxRate(mainPurpose) {
	return RESIDENTIAL_RE.test(String(mainPurpose || '')) ? 3.5 : 4.6;
}
export function isResidential(mainPurpose) {
	return RESIDENTIAL_RE.test(String(mainPurpose || ''));
}

export const REMODEL_LABELS = ['철거·가설', '내·외장 마감', '설비·전기(MEP)', '객실 가구·집기', '설계·인허가·감리'];
export const NEWBUILD_LABELS = ['기존 건물 철거', '건축 도급공사', '설계·감리', '인허가·부담금', '금융비용·예비비'];

// ═══ 산정근거 · 공공기관 출처 (③투자분석 / ④자금계획 인쇄 표기용) ═══
// 컴포넌트가 표의 '산정근거' 열과 하단 출처 각주로 렌더한다.
// 신축 5개 항목 근거(라벨과 1:1) — 건축 도급단가 기준 분해(철거는 기존 연면적 정액 30만/평).
// 지역보정은 표시 단가와 정합하도록 compute 단계에서 행별로 덧붙인다(아래 computeDevelopment 참고).
export const NEWBUILD_BASIS = [
	'기존 연면적 × 30만/평(정액)',
	'RC·표준 도급단가 × 용도·층수 보정',
	'도급단가 × 설계·감리 요율',
	'도급단가 × 인허가·부담금 2%',
	'도급단가 × 금융·예비비 5%',
];
// ③ 매입 부대비용 각주 — 법정 요율은 법령 직접 인용(번호는 표의 위첨자와 1:1). 표시 요율(상업용 4.6%/주택 3.5%)과 정합.
export const ACQ_SOURCES = [
	'취득세: 지방세법 제11조(부동산 취득의 세율) — 상업용(주택 외) 본세 4.0% + 지방교육세 0.4% + 농어촌특별세 0.2% = 4.6%. 주택은 가액별 1~3% 누진(제11조제1항제8호)에 지방교육세·농특세 가산 — 본 추정은 9억 초과·전용 85㎡ 초과 기준 3.5% 적용. 소관 행정안전부.',
	'중개보수: 공인중개사법 시행규칙 제20조 제4항 — 주택 외(상업용)은 거래금액의 0.9% 이내에서 협의. 소관 국토교통부·시·도 조례.',
	'법무·기타: 소유권이전 등록면허세(지방세법) + 법무사 보수(2003년 자율화). 등기·국민주택채권 매입 등 포함 정액 추정.',
];
// ④ 신축·리모델링 각주 — ⚠ 단가는 시장 추정치. 공공자료는 '교차검증·물가보정 참고'로만 표기(공공발표가로 오인 금지).
export const DEV_SOURCES = [
	'리모델링 평단가 = 유형별 표준 인테리어 단가를 공종 비중으로 분해한 기준단가 × 적용비율(기본 50%) × 지역보정. 신축 평단가 = RC·표준 도급단가를 항목별로 분해 × 지역보정.',
	'단가는 시장 실거래 기반 추정치이며, 한국부동산원 「건물신축단가표」(감정평가 재조달원가)·한국건설기술연구원 「건설공사비지수」(KOSIS)를 교차검증·물가보정 참고자료로 활용. RC조·표준마감·서울 기준, 부가가치세 10% 별도. 실제 견적은 현장 실측·시공사 견적에 따라 변동됩니다.',
];

// ②~⑤ 본문 페이지 하단 공통 법적 고지 (작게·회색). PrintPage 의 pageFooterLine1/2 와 동일 텍스트 — 단일 출처.
export const PAGE_FOOTER_LINES = [
	'* 본 자료에는 공개가 제한되거나 매도인의 비밀정보가 포함될 수 있습니다. 수신인이 불필요하게 수령한 경우 즉시 파기하여야 하며, WEONUS 및 매도인의 사전 서면 동의 없이 본 자료의 전부 또는 일부를 복제, 배포, 전송, 인용, 변경 또는 제3자에게 제공할 수 없습니다. 위반 시 관련 법령에 따른 민·형사상 책임이 발생할 수 있습니다.',
	'* 본 자료는 매도인의 진술 및 제공 자료를 기초로 작성되어 실제와 차이가 있을 수 있습니다.',
];

// ═══ spacevision 단가 테이블 (2026 상반기, 만원/평) ═══
// 신축 공사비 평당단가 [기본마감 min, 고급마감 max] — 구조 × 마감등급. 평균치 사용.
export const NEWBUILD_PRICE = {
	rc:      { basic: [460, 820], standard: [590, 1020], high: [740, 1320] },
	steel:   { basic: [420, 745], standard: [540, 950],  high: [690, 1225] },
	wood:    { basic: [435, 780], standard: [565, 995],  high: [722, 1295] },
	masonry: { basic: [350, 635], standard: [460, 815],  high: [595, 1055] },
};
// 신축 용도 보정계수
export const NEWBUILD_USE_MULT = {
	house: 1.0, multi: 0.95, apt: 0.92, commercial: 0.98, office: 1.02, factory: 0.75,
};
// 지역 보정계수(서울=1.0)
export const REGION_MULT = {
	'서울특별시': 1.0, '경기도': 0.93, '인천광역시': 0.91, '부산광역시': 0.88,
	'대구광역시': 0.86, '대전광역시': 0.86, '광주광역시': 0.85, '울산광역시': 0.86,
	'세종특별자치시': 0.88, '강원도': 0.82, '충청도': 0.83, '전라도': 0.82,
	'경상도': 0.83, '제주특별자치도': 0.88,
};
// 층수 보정계수
export const FLOOR_MULT = { 1: 0.95, 2: 0.97, 3: 1.0, 4: 1.03, 5: 1.06, '6+': 1.10 };
// 리모델링 인테리어 평당단가(만원/평) — 건물유형 × [기본, 표준, 고급]
export const REMODEL_TYPE_UNIT = {
	store_building:  [150, 200, 280],
	office_building: [180, 250, 350],
	house:           [250, 400, 600],
	fnb:             [280, 380, 520],
	clinic:          [350, 480, 650],
	hotel:           [400, 550, 750],
};
// 리모델링 공종 비중(합=1.0) — 5개 라벨 순서. 유형 단가를 항목별로 분해.
export const REMODEL_SPLIT = [0.10, 0.40, 0.25, 0.15, 0.10];
// 신축 보조항목 비율(도급 평당단가 대비) — 철거는 평당 정액.
export const DEMOLITION_UNIT_PP = 30;   // 기존건물 철거: 만원/평(기존 연면적 기준, 시장 추정)
export const NEWBUILD_PERMIT_RATE = 0.02;  // 인허가·부담금 ≈ 도급단가 2%
export const NEWBUILD_RESERVE_RATE = 0.05; // 금융비용·예비비 ≈ 도급단가 5%
export const SUPERVISION_MULT = 1.20;      // 설계+감리(설계 단독 대비 +20%)

// ═══ 편집기 노브 · 인쇄 가정 표기 (Phase 2) ═══
export const GRADE_LABELS = { basic: '기본', standard: '표준', high: '고급' }; // 마감 등급 → 표기명
export const GRADE_OPTIONS = Object.entries(GRADE_LABELS).map(([value, label]) => ({ value, label }));
export const REGION_OPTIONS = Object.keys(REGION_MULT); // 지역 보정 드롭다운(서울=1.0 기준)
export const PRICE_BASE_DATE = '2026년 상반기'; // 단가 기준 시점(spacevision/공공지수 참조 기준)

const avgPair = ([a, b]) => (Number(a) + Number(b)) / 2;
const regionMultOf = (key) => (REGION_MULT[key] != null ? REGION_MULT[key] : 1.0);

// 시공비 규모(만원)별 설계비율 [min%, max%] — 한국건축사협회+시장 표준(design.php).
export function getDesignFeeRate(constructionCostMan) {
	const eok = (Number(constructionCostMan) || 0) / 10000;
	if (eok < 3) return [7.0, 11.0];
	if (eok < 10) return [6.5, 9.0];
	if (eok < 30) return [5.5, 7.0];
	if (eok < 100) return [4.0, 5.5];
	if (eok < 500) return [3.0, 4.5];
	return [2.5, 3.5];
}

// main_purpose → 신축 용도키(NEWBUILD_USE_MULT)
export function mapNewbuildUse(mainPurpose) {
	const p = String(mainPurpose || '');
	if (/공장|창고|물류/.test(p)) return 'factory';
	if (/업무|오피스|사무/.test(p)) return 'office';
	if (/공동주택|다세대|연립|빌라/.test(p)) return 'apt';
	if (/다가구|원룸/.test(p)) return 'multi';
	if (/단독주택|전원/.test(p)) return 'house';
	return 'commercial'; // 근린생활·판매·숙박·의료 등 비주거 일반
}
// main_purpose → 리모델링 유형키(REMODEL_TYPE_UNIT)
export function mapRemodelType(mainPurpose) {
	const p = String(mainPurpose || '');
	if (/숙박|호텔|호스텔|모텔|콘도|펜션/.test(p)) return 'hotel';
	if (/의료|병원|의원|클리닉/.test(p)) return 'clinic';
	if (/음식|카페|식당|휴게|제과/.test(p)) return 'fnb';
	if (/업무|오피스|사무/.test(p)) return 'office_building';
	if (/주택|아파트|공동주택|다가구|단독|주거/.test(p)) return 'house';
	return 'store_building'; // 근린생활·판매·상가 등
}
// item → 층수보정 키
export function floorKeyFromItem(item) {
	const f = Number((item && item.floor_cnt_F) || 0);
	if (f >= 6) return '6+';
	if (f >= 1 && f <= 5) return f;
	return 3; // 미상 → 3층(보정 1.0)
}

// 신축 5개 항목 평당단가(만원/평, 서울 기준) 산출. 도급 = 평균단가 × 용도 × 층수.
// region: 설계비율 '구간 선택'에만 쓰는 지역보정(평당단가 자체엔 미적용 — 지역보정은 compute 단계).
export function buildNewbuildUnit(struct, grade, useKey, floorKey, nbAreaP, region = 1.0) {
	const table = (NEWBUILD_PRICE[struct] || NEWBUILD_PRICE.rc);
	const base = avgPair(table[grade] || table.standard);
	const useM = NEWBUILD_USE_MULT[useKey] != null ? NEWBUILD_USE_MULT[useKey] : 1.0;
	const floorM = FLOOR_MULT[floorKey] != null ? FLOOR_MULT[floorKey] : 1.0;
	const contractPP = Math.round(base * useM * floorM); // 건축 도급공사 평당(서울 기준)
	// 설계비율 구간은 지역보정 반영한 실제 시공비 규모로 선택(지방 프로젝트의 구간 오선택 방지).
	const constructionMan = contractPP * (Number(region) || 1.0) * (Number(nbAreaP) || 0);
	const feeRate = avgPair(getDesignFeeRate(constructionMan)) / 100;
	const designPP = Math.round(contractPP * feeRate * SUPERVISION_MULT); // 설계·감리 평당(서울 기준)
	const permitPP = Math.round(contractPP * NEWBUILD_PERMIT_RATE);       // 인허가·부담금 평당
	const reservePP = Math.round(contractPP * NEWBUILD_RESERVE_RATE);     // 금융·예비비 평당
	return [DEMOLITION_UNIT_PP, contractPP, designPP, permitPP, reservePP];
}
// 리모델링 5개 항목 평당단가(만원/평, 서울·100% 기준). 유형 단가를 공종 비중으로 분해.
export function buildRemodelUnit(typeKey, grade) {
	const table = REMODEL_TYPE_UNIT[typeKey] || REMODEL_TYPE_UNIT.store_building;
	const gi = grade === 'basic' ? 0 : grade === 'high' ? 2 : 1;
	const total = Number(table[gi]) || 0;
	return REMODEL_SPLIT.map(r => Math.round(total * r));
}

// ═══ 자금계획 행(remodelRows/newbuildRows) 모델 — { key, label, unit } ═══
// 표준 행 키 = rm0..rm4 / nb0..nb4 (라벨·산정근거 인덱스와 1:1). 커스텀 추가행 = rmc{n}/nbc{n}.
// 신축 철거행(nb0)은 compute 에서 정액·기존연면적·지역보정 제외 특수처리 — 키로 식별하므로 가감/순서에 무관.
// 표준 키에서 인덱스 추출(표준행이면 0..n, 커스텀/손상이면 -1).
function stdRowIndex(key, prefix) {
	const m = new RegExp('^' + prefix + '(\\d+)$').exec(String(key == null ? '' : key));
	return m ? Number(m[1]) : -1;
}
// 행 배열에서 충돌 없는 다음 커스텀 키(rmc{n}/nbc{n}). 편집기 '항목 추가'가 사용.
export function nextCustomKey(rows, kind) {
	const prefix = kind === 'newbuild' ? 'nbc' : 'rmc';
	let max = 0;
	for (const r of (Array.isArray(rows) ? rows : [])) {
		const m = new RegExp('^' + prefix + '(\\d+)$').exec(String(r && r.key != null ? r.key : ''));
		if (m) max = Math.max(max, Number(m[1]));
	}
	return prefix + (max + 1);
}
// 표준 단가배열로 행을 (재)생성: 행 없으면 표준행 시드, 있으면 표준키 단가만 갱신(라벨·커스텀행·삭제 보존).
function refreshOrSeedRows(rows, labels, units, prefix) {
	if (!Array.isArray(rows) || rows.length === 0) {
		return labels.map((label, i) => ({ key: prefix + i, label, unit: units[i] }));
	}
	return rows.map(r => {
		const idx = stdRowIndex(r.key, prefix);
		return (idx >= 0 && idx < units.length) ? { ...r, unit: units[idx] } : r;
	});
}

export const DEFAULT_NOTE = 'AI추정가는 토지·건물 원가 기준입니다. 매매가와의 차액은 공실 없이 즉시 매출이 발생하는 운영가치로 볼 수 있습니다.';

// 가정키(구조·등급·용도·층수·지역·용적률)로부터 신축/리모델링 '기준단가' 배열을 (재)생성한다.
// buildDefaults 시드 · 편집기 등급 변경 · URL 복원(applyInvestmentQuery)이 공유. 단가는 서울·100% 기준
// (지역·적용비율은 computeDevelopment 단계 보정). 등급만이 기준단가 자체를 바꾸는 유일한 레버.
export function regenerateUnits(s) {
	const nbAreaP = Math.round((Number(s.landAreaP) || 0) * (Number(s.newbuildFar) || 0) / 100);
	const remodelUnits = buildRemodelUnit(s.remodelTypeKey, s.gradeKey);
	// 기준단가는 항상 서울·1.0 으로 생성(지역 중립) — 지역보정은 computeDevelopment 의 compute-time 승수로만 적용.
	// region 을 넘기면 설계비율 구간 선택에 지역이 섞여 newbuild 설계·감리행이 지역 의존 → 등급 재생성이
	// 편집 순서(지역→등급 vs 등급→지역)에 따라 달라짐(codex P2). buildDefaults 도 항상 서울 시드라 동작 불변.
	const newbuildUnits = buildNewbuildUnit(s.structKey, s.gradeKey, s.useKey, s.floorKey, nbAreaP);
	// 표준키 단가만 갱신 — 사용자가 추가한 커스텀행·이름변경한 라벨·삭제한 행은 보존(등급 변경 ≠ 전체 초기화).
	s.remodelRows = refreshOrSeedRows(s.remodelRows, REMODEL_LABELS, remodelUnits, 'rm');
	s.newbuildRows = refreshOrSeedRows(s.newbuildRows, NEWBUILD_LABELS, newbuildUnits, 'nb');
	return s;
}

export function buildDefaults(item) {
	item = item || {};
	const s = {
		sellMan: Number(item.sell_price) || 0,
		taxRate: classifyAcqTaxRate(item.main_purpose),
		brokerRate: 0.9,
		legalFee: 1000,
		ltv: 60,
		loanRate: 5.0,
		noi: Math.round((Number(item.total_income) || 0) * 12),
		// 신축/리모델링 가정(편집 가능). 서울 기준; 지역·적용비율은 compute 단계 보정.
		structKey: 'rc',
		gradeKey: 'standard',
		regionKey: '서울특별시',
		floorKey: floorKeyFromItem(item),
		useKey: mapNewbuildUse(item.main_purpose),
		remodelTypeKey: mapRemodelType(item.main_purpose),
		remodelRatio: 50, // 인테리어 적용 비율(%) — spacevision 표준 기본값
		remodelRows: [],  // [{key,label,unit}] — regenerateUnits 가 가정키로 표준행 시드
		newbuildRows: [],
		newbuildFar: Number(item.fa_ratio) || 0,
		note: DEFAULT_NOTE,
		remodelAreaP: Number(item.total_size_p) || 0,
		landAreaP: Number(item.land_size_p) || 0,
	};
	return regenerateUnits(s); // 가정키로 remodelRows/newbuildRows 표준행 시드
}

export function computeAcquisition(s) {
	const sell = Number(s.sellMan) || 0;
	const tax = Math.round(sell * (Number(s.taxRate) || 0) / 100);
	const broker = Math.round(sell * (Number(s.brokerRate) || 0) / 100);
	const legal = Math.round(Number(s.legalFee) || 0);
	const addons = tax + broker + legal;
	const total = sell + addons;
	const loan = Math.round(sell * (Number(s.ltv) || 0) / 100);
	const interest = Math.round(loan * (Number(s.loanRate) || 0) / 100);
	const equity = total - loan;
	const cashflow = Math.round((Number(s.noi) || 0) - interest);
	const roe = equity > 0 ? (cashflow / equity) * 100 : null;
	return { sell, tax, broker, legal, addons, total, loan, interest, equity, cashflow, roe };
}

export function computeDevelopment(s) {
	const rmArea = Number(s.remodelAreaP) || 0;
	const nbArea = Math.round((Number(s.landAreaP) || 0) * (Number(s.newbuildFar) || 0) / 100);
	const region = regionMultOf(s.regionKey);
	// 적용비율: 결측/비정상('', null, NaN)만 100% 폴백. 유효 0% 는 그대로 유지(리모델링 미실행 모델링 허용).
	const rawRatio = s.remodelRatio;
	const ratio = (rawRatio == null || rawRatio === '' || !Number.isFinite(Number(rawRatio)) ? 100 : Number(rawRatio)) / 100;
	const ratioPct = Math.round(ratio * 100);
	// 산정근거 표기용 지역보정 꼬리(서울=1.0 이면 생략) — 표시 단가와 정확히 정합시키기 위함.
	const regionTxt = region !== 1 ? ` × 지역 ${Number(region.toFixed(2))}` : '';
	// 리모델링: 유효단가 = 기준단가 × 적용비율 × 지역보정. unit×면적 = amount 가 일관되도록 유효단가 표시.
	// basis 는 위 산식(기준단가·적용비율·지역보정)을 그대로 노출해 표시 평단가와 정합(codex P2). 행 가감/이름변경 무관.
	const remodelRows = (s.remodelRows || []).map(r => {
		const base = Number(r.unit) || 0;
		const unit = Math.round(base * ratio * region);
		return { label: r.label, unit, amount: Math.round(unit * rmArea), basis: `기준단가 ${base}만 × 적용 ${ratioPct}%${regionTxt}` };
	});
	const remodelTotal = remodelRows.reduce((a, r) => a + r.amount, 0);
	// 신축: 유효단가 = 기준단가 × 지역보정. 단, 철거행(키 nb0)은 정액(지역보정 제외) + 기존 연면적 기준.
	// 근거(basis)는 표준키(nb0..nb4) → NEWBUILD_BASIS 인덱스, 커스텀행은 '사용자 입력 단가'. 키 기반이라 순서/가감 무관.
	const newbuildRows = (s.newbuildRows || []).map(r => {
		const base = Number(r.unit) || 0;
		const isDemo = r.key === 'nb0';
		const idx = stdRowIndex(r.key, 'nb');
		const unit = isDemo ? base : Math.round(base * region);
		const area = isDemo ? rmArea : nbArea;
		const basisBase = (idx >= 0 && idx < NEWBUILD_BASIS.length) ? NEWBUILD_BASIS[idx] : '사용자 입력 단가';
		const basis = isDemo ? basisBase : `${basisBase}${regionTxt}`;
		return { label: r.label, unit, amount: Math.round(unit * area), basis };
	});
	const newbuildTotal = newbuildRows.reduce((a, r) => a + r.amount, 0);
	// 인쇄 표기용 가정 요약(④ 하단 '가정' 줄) — 표시 단가를 만든 등급·지역·적용비율·단가기준일.
	const assumptions = {
		gradeKey: s.gradeKey,
		gradeLabel: GRADE_LABELS[s.gradeKey] || s.gradeKey,
		regionKey: s.regionKey,
		regionLabel: String(s.regionKey || '').replace(/(특별자치시|특별자치도|특별시|광역시|도)$/, '') || s.regionKey,
		ratioPct,
		baseDate: PRICE_BASE_DATE,
	};
	return { rmArea, nbArea, remodelRows, remodelTotal, newbuildRows, newbuildTotal, assumptions };
}

export function computeEstimateBadge(sellMan, estimateTotalMan) {
	const sell = Number(sellMan) || 0;
	const est = Number(estimateTotalMan) || 0;
	if (est <= 0) return null;
	if (sell < est) {
		const pct = Math.round((est - sell) / est * 100);
		return { kind: 'under', pct, text: `저평가 ${pct}%↓`, comparePyText: `추정가 대비 ${pct}% 낮음` };
	}
	if (sell > est) {
		const diffMan = sell - est;
		const eok = Math.round(diffMan / 10000);
		return { kind: 'premium', diffMan, eok, text: `운영가치 약 ${eok}억`, comparePyText: '' };
	}
	return { kind: 'equal', pct: 0, text: '추정가와 동일', comparePyText: '' };
}

// 출력 기본값에 실을 추정가 키. 담당자가 고친 것만 싣는다.
//   estimate: 화면 추정가 {available,total,py}(만원), avm: 이번에 받은 블록 값 {total,py} 또는 null,
//   show: 표시 여부, showDefault: 기본 규칙(추정가 > 매매가)이 정하는 표시 여부.
// 손대지 않은 블록 값을 저장하면 그 순간의 값이 굳어, 필지 합산·모델 갱신이 출력물에 닿지 않는다
// (2026-09-29: 2필지 이상 매물 11건에 1필지 값이 굳어 있었다). 블록 값을 못 받은 세션(avm 없음)은
// 화면 값을 그대로 싣는다. 그 값은 예전 저장값일 수 있고, 빼면 저장 한 번에 담당자의 수정이 지워진다.
// 추정가가 아직 없거나 받지 못한 화면에서는 판정할 재료가 없으므로 이미 저장된 추정가 키(saved)를
// 그대로 옮긴다. 저장은 설정 전체를 바꿔 쓰므로, 옮기지 않으면 표시 여부만 저장된 행이 지워진다.
export function estimateSettings(estimate, avm, show, showDefault, saved) {
	const s = {};
	if (!estimate || !estimate.available) {
		const prev = saved && typeof saved === 'object' ? saved : {};
		['est_total', 'est_py', 'est_show'].forEach(k => { if (prev[k] != null) s[k] = String(prev[k]); });
		return s;
	}
	const t = Math.round(Number(estimate.total) || 0);
	const p = Math.round(Number(estimate.py) || 0);
	if (!avm || avm.total !== t || avm.py !== p) {
		s.est_total = String(t);
		s.est_py = String(p);
	}
	if (!!show !== !!showDefault) s.est_show = show ? '1' : '0';
	return s;
}

export function computeFundingComparison(acq, dev) {
	return {
		buyOnly: acq.total,
		buyRemodel: acq.total + dev.remodelTotal,
		buyNewbuild: acq.total + dev.newbuildTotal,
	};
}

export const QUERY_KEYS = {
	taxRate: 'tax_rate', brokerRate: 'broker_rate', legalFee: 'legal_fee',
	ltv: 'ltv', loanRate: 'loan_rate', noi: 'noi', newbuildFar: 'nb_far',
	gradeKey: 'grade', regionKey: 'region', remodelRatio: 'rmr', note: 'inv_note',
};

// 행 배열 → 컴팩트 JSON([[key,label,unit],...]). 빈 배열이면 null. 단가는 숫자(빈칸은 '' 보존).
function serializeRows(rows) {
	if (!Array.isArray(rows) || rows.length === 0) return null;
	const arr = rows.map(r => {
		const n = Number(r && r.unit);
		const unit = (r && (r.unit === '' || r.unit == null)) || !Number.isFinite(n) ? '' : n;
		return [String(r && r.key != null ? r.key : ''), String(r && r.label != null ? r.label : ''), unit];
	});
	return JSON.stringify(arr);
}

export function serializeInvestment(state) {
	const q = {};
	for (const [k, key] of Object.entries(QUERY_KEYS)) {
		if (state[k] != null && state[k] !== '') q[key] = String(state[k]);
	}
	const rm = serializeRows(state.remodelRows);
	const nb = serializeRows(state.newbuildRows);
	if (rm) q.rmrows = rm;
	if (nb) q.nbrows = nb;
	return q;
}

// serializeInvestment 가 내보낼 수 있는 모든 쿼리키(빈값 생략 특성상, URL 갱신 전 stale 제거용 삭제목록).
// 레거시 rm1-5/nb1-5 도 포함 — 구 저장 디폴트/구 URL 이 병합돼 남은 키를 동기화 시 함께 청소.
export const ALL_QUERY_KEYS = [
	...Object.values(QUERY_KEYS),
	'rmrows', 'nbrows',
	...[1, 2, 3, 4, 5].map(i => 'rm' + i),
	...[1, 2, 3, 4, 5].map(i => 'nb' + i),
];

// 손상/구버전 URL 의 비정상 숫자(예: ltv=abc → NaN)는 무시하고 기본값을 유지한다.
// 그렇지 않으면 NaN 이 편집칸·URL 재직렬화로 새어나간다(calc 는 ||0 로 안전하지만 표시가 깨짐).
const setNum = (state, k, raw) => { if (raw === '') return; const n = Number(raw); if (Number.isFinite(n)) state[k] = n; };

// rmrows/nbrows 파싱 → 검증된 행 배열 또는 null. 문자열(URL)·객체(printDefaults) 모두 허용.
function parseRowsParam(raw) {
	if (raw == null) return null;
	let arr = raw;
	if (typeof raw === 'string') { try { arr = JSON.parse(raw); } catch (e) { return null; } }
	if (!Array.isArray(arr) || arr.length === 0) return null;
	return arr;
}
// 파싱 배열 → 정규화 {key,label,unit}(행수≤20·라벨≤40·키≤12 상한, 손상행 스킵). 외부 입력(URL·DB) 방어.
// 빈 키·중복 키는 충돌 없는 커스텀 키로 치환 — Vue :key 유일성 보장(소스가 URL이든 DB든 단일 정규화 지점).
function rebuildRows(arr, prefix) {
	const kind = prefix === 'nb' ? 'newbuild' : 'remodel';
	const out = [];
	const seen = new Set();
	for (let i = 0; i < arr.length && out.length < 20; i++) {
		const r = arr[i];
		let key, label, unit;
		if (Array.isArray(r)) { key = r[0]; label = r[1]; unit = r[2]; }
		else if (r && typeof r === 'object') { key = r.key; label = r.label; unit = r.unit; }
		else continue;
		key = String(key == null ? '' : key).slice(0, 12);
		if (key === '' || seen.has(key)) key = nextCustomKey(out, kind); // 빈/중복 키 → 충돌 없는 커스텀 키
		seen.add(key);
		label = String(label == null ? '' : label).slice(0, 40);
		const n = Number(unit);
		out.push({ key, label, unit: (unit === '' || unit == null || !Number.isFinite(n)) ? '' : n });
	}
	return out;
}
// 레거시 rm1-5/nb1-5(1-based) → 표준행(키 rm0..rm4, 0-based) 단가 per-index override. 구 디폴트/구 URL 하위호환.
function applyLegacyUnits(rows, query, prefix) {
	if (!Array.isArray(rows)) return;
	for (let i = 0; i < 5; i++) {
		const v = query[prefix + (i + 1)];
		if (v == null) continue;
		const row = rows.find(r => r.key === prefix + i);
		if (row) setNum(row, 'unit', v);
	}
}

export function applyInvestmentQuery(state, query) {
	query = query || {};
	let regenFromGrade = false;
	for (const [k, key] of Object.entries(QUERY_KEYS)) {
		if (query[key] == null) continue;
		if (k === 'gradeKey') {
			const v = String(query[key]);
			if (GRADE_LABELS[v] != null) { state.gradeKey = v; regenFromGrade = true; } // 등급은 기준단가 재생성 트리거
		} else if (k === 'regionKey') {
			const v = String(query[key]);
			if (REGION_MULT[v] != null) state.regionKey = v; // 지역은 compute 단계 보정(재생성 불필요)
		} else if (k === 'note') {
			state[k] = String(query[key]);
		} else {
			setNum(state, k, query[key]);
		}
	}
	// 등급이 URL 로 바뀌면 기준단가 배열 재생성 — 단, 수동 per-row(rm/nb) 편집이 우선하도록 그 전에 수행.
	// (구 URL 은 grade 파라미터가 없어 재생성이 안 일어남 → 기존 동작/시드값 그대로 = 하위호환.)
	if (regenFromGrade) regenerateUnits(state);
	// 신규 rmrows/nbrows = 전체 행 스냅샷(권위적). 없을 때만 레거시 rm1-5/nb1-5 per-index override 로 폴백.
	const rmRows = parseRowsParam(query.rmrows);
	if (rmRows) state.remodelRows = rebuildRows(rmRows, 'rm');
	else applyLegacyUnits(state.remodelRows, query, 'rm');
	const nbRows = parseRowsParam(query.nbrows);
	if (nbRows) state.newbuildRows = rebuildRows(nbRows, 'nb');
	else applyLegacyUnits(state.newbuildRows, query, 'nb');
	return state;
}
